import axiosClient from '@/api/axiosClient';
import type { QueryColumn } from '@/api/contracts/queryResult';
import type { FreeTextSearch } from '@/api/contracts/dataModel';

export interface TableQueryRequest {
  page?: number;
  pageSize?: number;
  additionalFilters?: Array<{
    tableAliasOrName: string;
    columnName: string;
    operator: string;
    value?: string | null;
  }>;
  orderByOverride?: Array<{
    tableAliasOrName: string;
    columnName: string;
    ascending: boolean;
  }>;
  freeText?: FreeTextSearch | null;
}

export interface TableQueryResponse {
  columns: QueryColumn[];
  rows: Record<string, unknown>[];
  page: number;
  pageSize: number;
  total: number;
  tableKey: string;
}

export interface UpdateTableCellRequest {
  rowIds: number[];
  columnKey: string;
  newValue: unknown;
  operation?: 'replace' | 'appendComment';
}

export interface UpdateTableCellResponse {
  updatedRows: number;
}

export interface AddTableRowsRequest {
  rows: Record<string, unknown>[];
  fieldActions?: Record<string, string>;
}

// WI 2184: id-ene til de nyopprettede radene (ICSRowId_), i samme rekkefølge som request.rows.
// Tom hvis tabellen ikke har ICSRowId_-identitetskolonne.
export interface AddTableRowsResponse {
  insertedRows: number;
  createdRowIds: number[];
}

export interface CopyTableRowsRequest {
  sourceRowIds: number[];
  fieldActions?: Record<string, string>;
  fieldValues?: Record<string, unknown>;
}

export interface CopyTableRowsResponse {
  insertedRows: number;
}

export interface DeleteTableRowsRequest {
  rowIds: number[];
}

export interface DeleteTableRowsResponse {
  deletedRows: number;
}

export interface RestoreTableRowsRequest {
  rowIds: number[];
}

export interface RestoreTableRowsResponse {
  restoredRows: number;
}

// Task 2351: resultat av eksplisitt «Regenerer løpenummer» på en eksisterende tabell.
export interface RegenerateSequenceResponse {
  assignedRows: number;
}

export async function updateTableCell (
  tableKeyOrId: string | number,
  body: UpdateTableCellRequest
): Promise<UpdateTableCellResponse> {
  const tableRef = String(tableKeyOrId ?? '').trim();
  const { data } = await axiosClient.patch<UpdateTableCellResponse>(
    `v2/table/${tableRef}/rows/cell`,
    body
  );
  return data;
}

export async function addTableRows (
  tableKeyOrId: string | number,
  body: AddTableRowsRequest
): Promise<AddTableRowsResponse> {
  const tableRef = String(tableKeyOrId ?? '').trim();
  const { data } = await axiosClient.post<AddTableRowsResponse>(`v2/table/${tableRef}/rows`, body);
  return data;
}

export async function getTableRow (
  tableKeyOrId: string | number,
  rowId: number
): Promise<Record<string, unknown> | null> {
  const tableRef = String(tableKeyOrId ?? '').trim();
  const { data } = await axiosClient.get<Record<string, unknown> | null>(
    `v2/table/${tableRef}/rows/${rowId}`
  );
  return data ?? null;
}

export async function copyTableRows (
  tableKeyOrId: string | number,
  body: CopyTableRowsRequest
): Promise<CopyTableRowsResponse> {
  const tableRef = String(tableKeyOrId ?? '').trim();
  const { data } = await axiosClient.post<CopyTableRowsResponse>(
    `v2/table/${tableRef}/rows/copy`,
    body
  );
  return data;
}

export async function deleteTableRows (
  tableKeyOrId: string | number,
  body: DeleteTableRowsRequest
): Promise<DeleteTableRowsResponse> {
  const tableRef = String(tableKeyOrId ?? '').trim();
  const { data } = await axiosClient.delete<DeleteTableRowsResponse>(
    `v2/table/${tableRef}/rows`,
    { data: body }
  );
  return data;
}

export async function restoreTableRows (
  tableKeyOrId: string | number,
  body: RestoreTableRowsRequest
): Promise<RestoreTableRowsResponse> {
  const tableRef = String(tableKeyOrId ?? '').trim();
  const { data } = await axiosClient.post<RestoreTableRowsResponse>(
    `v2/table/${tableRef}/rows/restore`,
    body
  );
  return data;
}

// Task 2351: eksplisitt «Regenerer løpenummer» — fyller blanke løpenummer-celler på tabellens
// egne rader (faktiske verdier bevares). Ingen request-body; tabellens config styrer tildelingen.
export async function regenerateTableSequence (
  tableKeyOrId: string | number
): Promise<RegenerateSequenceResponse> {
  const tableRef = String(tableKeyOrId ?? '').trim();
  const { data } = await axiosClient.post<RegenerateSequenceResponse>(
    `v2/table/${tableRef}/regenerate-sequence`
  );
  return data;
}
